export const LABEL_CSS_CLASSES = {
  LABEL: 'UI_CAPTION_SEMIBOLD_DF', // Label text for pie slices
} as const;

export interface BasePieChartUIConfigs {
  // ===== LABEL CONFIGURATION =====
  labelMinPercentThreshold: number; // Minimum percentage to show label (e.g., 0.05 = 5%)
  labelPositionRatio: number; // Position of label within slice (0.5 = center of slice)
  labelCssClass: string; // CSS class for labels

  // ===== ACTIVE SHAPE (HOVER) CONFIGURATION =====
  activeSliceGrowth: number; // Growth amount when slice is active/hovered
  activeShapeOuterRadiusIncrease: number; // Additional outer radius increase for active shape
  activeShapeTransitionDuration: string; // Transition duration for active shape animation

  // ===== CELL (SLICE) CONFIGURATION =====
  dimmedOpacity: number; // Opacity for non-active slices when another is hovered
  cellTransitionDuration: string; // Transition duration for cell opacity changes

  // ===== MULTI-LEVEL PIE CHART CONFIGURATION =====
  // Outer pie (parent level) - percentage values for proper alignment
  multiLevelOuterPieOuterRadius: string; // Outer radius of outer pie (e.g., "51.5%")
  multiLevelOuterPieInnerRadius: string; // Inner radius of outer pie, creates donut effect (e.g., "25%")
  
  // Inner pie (child level) - percentage values for proper alignment
  multiLevelInnerPieInnerRadius: string; // Inner radius of inner pie (e.g., "52%")
  multiLevelInnerPieOuterRadius: string; // Outer radius of inner pie (e.g., "80%")
  
  // Gap between levels is calculated as: outerPieOuterRadius - innerPieInnerRadius
  // Current: 51.5% - 52% = -0.5% (negative means inner pie extends slightly beyond outer pie for visual connection)

  // ===== COLOR SHADING CONFIGURATION =====
  // For multi-level charts: children use progressively lighter shades of parent color
  childColorBaseLightnessIncrease: number; // Base lightness increase for first child (e.g., 15%)
  childColorLightnessIncrement: number; // Additional lightness increase per subsequent child (e.g., 10%)
  childColorMaxLightness: number; // Maximum lightness to prevent colors from becoming too light (e.g., 95%)

  // ===== CONTAINER CONFIGURATION =====
  defaultContainerHeight: string; // Default height for pie chart container (e.g., "500px")
  
  // ===== DYNAMIC RADIUS CONFIGURATION =====
  maxOuterRadius: string; // Maximum outer radius for outermost level (e.g., "80%")
  levelGap: string; // Gap between levels (e.g., "2%")
  minInnerRadius: string; // Minimum inner radius for innermost level (e.g., "0%")
  innermostRingInnerRadius: string; // Inner radius for the innermost ring (Level 0) - should be > 0% to make it a ring (e.g., "15%")
}

/**
 * if any changes are being made to the spacing and such of 
 * this comps design, all these properties need to be carefully 
 * observed and tweaked to ensure no ui has gotten warped due to sideeffects
 * 
 * IMPORTANT: The bounds (height and width) of wrappers are critical for pie chart
 * to render correctly. ResponsiveContainer relies on these bounds.
 */
export const _base_piechart_ui_configs: BasePieChartUIConfigs = {
  // ===== LABEL CONFIGURATION =====
  labelMinPercentThreshold: 0.05, // Only show label if slice is >= 5% of total
  labelPositionRatio: 0.5, // Position label at center of slice (50% between inner and outer radius)
  labelCssClass: 'UI_CAPTION_SEMIBOLD_DF', // CSS class for label text

  // ===== ACTIVE SHAPE (HOVER) CONFIGURATION =====
  activeSliceGrowth: 1, // Growth amount in pixels when slice is active
  activeShapeOuterRadiusIncrease: 2, // Additional outer radius increase for active shape (pixels)
  activeShapeTransitionDuration: "all 0.4s ease-out", // Smooth transition for active shape

  // ===== CELL (SLICE) CONFIGURATION =====
  dimmedOpacity: 0.5, // Opacity for non-active slices when another slice is hovered
  cellTransitionDuration: "opacity 0.3s ease-out", // Smooth transition for opacity changes

  // ===== MULTI-LEVEL PIE CHART CONFIGURATION =====
  // Outer pie (parent level) - percentage-based for proper alignment
  multiLevelOuterPieOuterRadius: "51.5%", // Outer radius of outer pie
  multiLevelOuterPieInnerRadius: "25%", // Inner radius creates donut effect
  
  // Inner pie (child level) - percentage-based for proper alignment
  multiLevelInnerPieInnerRadius: "52%", // Inner radius of inner pie (slightly overlaps outer pie)
  multiLevelInnerPieOuterRadius: "80%", // Outer radius of inner pie
  
  // Gap calculation: 51.5% - 52% = -0.5% (negative means slight overlap for visual connection)

  // ===== COLOR SHADING CONFIGURATION =====
  // Children get progressively lighter shades: base + (index * increment)
  // Example: Child 1 = +15%, Child 2 = +25%, Child 3 = +35%, etc.
  childColorBaseLightnessIncrease: 15, // Base lightness increase for first child
  childColorLightnessIncrement: 10, // Additional lightness per subsequent child
  childColorMaxLightness: 95, // Maximum lightness to maintain visibility

  // ===== CONTAINER CONFIGURATION =====
  defaultContainerHeight: "500px", // Default height for pie chart container
  
  // ===== DYNAMIC RADIUS CONFIGURATION =====
  maxOuterRadius: "80%", // Maximum outer radius for outermost level
  levelGap: "0.5%", // Gap between levels
  minInnerRadius: "0%", // Minimum inner radius for innermost level (not used for multi-level)
  innermostRingInnerRadius: "15%", // Inner radius for Level 0 (innermost ring) - makes it a donut/ring shape
};

/**
 * Calculate radius ranges for N levels dynamically
 * @param numLevels - Number of levels (1, 2, 3, ...)
 * @returns Array of { innerRadius, outerRadius } for each level (outermost to innermost)
 */
export const calculateRadiusRanges = (numLevels: number): Array<{ innerRadius: string; outerRadius: string }> => {
  const config = _base_piechart_ui_configs;
  
  if (numLevels === 1) {
    // Single level: use calculated responsive values (handled in component)
    return [{ innerRadius: "0%", outerRadius: "80%" }];
  }
  
  // Parse percentage values
  const parsePercent = (val: string): number => parseFloat(val.replace('%', ''));
  const formatPercent = (val: number): string => `${val}%`;
  
  const maxOuter = parsePercent(config.maxOuterRadius || "80%");
  const innermostInner = parsePercent(config.innermostRingInnerRadius || "15%");
  const gap = parsePercent(config.levelGap || "2%");
  
  // Calculate available space (from innermost ring's inner radius to max outer)
  const totalSpace = maxOuter - innermostInner;
  const totalGaps = (numLevels - 1) * gap;
  const spacePerLevel = (totalSpace - totalGaps) / numLevels;
  
  // Generate radius ranges from innermost (Level 0) to outermost (Level N)
  // Level 0 (parent) is innermost, children expand outward
  // Level 0 always has innerRadius = innermostRingInnerRadius to make it a ring
  const ranges: Array<{ innerRadius: string; outerRadius: string }> = [];
  
  for (let i = 0; i < numLevels; i++) {
    if (i === 0) {
      // Level 0: starts at innermostRingInnerRadius
      const outerRadius = innermostInner + spacePerLevel;
      ranges.push({
        innerRadius: formatPercent(innermostInner),
        outerRadius: formatPercent(outerRadius),
      });
    } else {
      // Subsequent levels: continue outward
      const innerRadius = innermostInner + (i * (spacePerLevel + gap));
      const outerRadius = innerRadius + spacePerLevel;
      
      ranges.push({
        innerRadius: formatPercent(innerRadius),
        outerRadius: formatPercent(Math.min(maxOuter, outerRadius)),
      });
    }
  }
  
  return ranges;
};

